import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';

export interface ValidationActions {
  // User status validation
  checkUserStatus: (user: User) => Promise<boolean>;
  
  // Session validation utilities
  validateSession: (session: Session) => Promise<boolean>;
  checkSessionValidity: () => Promise<boolean>;
  recoverSession: () => Promise<boolean>;
  
  // Network error detection
  isNetworkError: (error: any) => boolean;
}

export const createValidationActions = (set: any, get: any) => ({
  // Check if user has valid profile or is on waitlist
  checkUserStatus: async (user: User): Promise<boolean> => {
    try {
      logger.info('AuthStore', 'Checking user status', { userId: user.id, email: user.email });
      
      // First check if user has a profile
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();
        
      if (!profileError && profile) {
        logger.info('AuthStore', 'User has valid profile');
        return true;
      }
      
      // Check if user is on waitlist
      const { data: waitlistEntry, error: waitlistError } = await supabase
        .from('user_waitlist')
        .select('email')
        .eq('email', user.email || '')
        .maybeSingle();
        
      if (!waitlistError && waitlistEntry) {
        logger.info('AuthStore', 'User is on waitlist, showing notification');
        
        // Set waitlist dialog state directly in store
        set({ waitlist: { showDialog: true, email: user.email || '' } });
        
        // Sign out the user since they're on waitlist
        await supabase.auth.signOut();
        return false;
      }
      
      logger.warn('AuthStore', 'User not found in profiles or waitlist', { 
        profileError: profileError?.message,
        waitlistError: waitlistError?.message 
      });
      
      return false;
    } catch (error) {
      logger.error('AuthStore', 'Error checking user status', { 
        error: error instanceof Error ? error.message : String(error) 
      });
      return false;
    }
  },

  // Enhanced session validation with integrity checks
  validateSession: async (sessionToValidate: Session): Promise<boolean> => {
    try {
      // Basic session validation - check if session exists and has valid user
      if (!sessionToValidate?.user?.id || !sessionToValidate?.access_token) {
        logger.warn('AuthStore', 'Session validation failed - invalid session structure');
        return false;
      }
      
      // Check if session is expired
      const now = Math.floor(Date.now() / 1000);
      if (sessionToValidate.expires_at && sessionToValidate.expires_at < now) {
        logger.warn('AuthStore', 'Session validation failed - session expired');
        return false;
      }
      
      logger.debug('AuthStore', 'Session validation successful', { 
        userId: sessionToValidate.user.id,
        email: sessionToValidate.user.email,
        hasToken: !!sessionToValidate.access_token
      });
      
      return true;
    } catch (error: any) {
      logger.error('AuthStore', 'Session validation error', { 
        error: error instanceof Error ? error.message : String(error),
        userId: sessionToValidate?.user?.id,
        email: sessionToValidate?.user?.email 
      });
      return false;
    }
  },

  // Check if current session is valid by making an API call
  checkSessionValidity: async () => {
    const state = get();
    if (!state.session || !state.user) return false;

    try {
      // Try to fetch user profile to validate session
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('id', state.user.id)
        .single();

      if (error) {
        // Smart error handling: distinguish network issues from auth failures
        if (get().isNetworkError(error)) {
          logger.debug('AuthStore', 'Session validation failed due to network issue, not logging out', { 
            error: error.message 
          });
          // Return false but don't logout - let the app gracefully handle connectivity issues
          return false;
        }

        // Real auth failure (401, expired token, etc.) - logout is appropriate
        logger.warn('AuthStore', 'Session validation failed due to auth issue, signing out', { 
          error: error.message 
        });
        await get().signOut();
        return false;
      }

      return true;
    } catch (error) {
      // Handle network errors gracefully in catch block too
      if (get().isNetworkError(error)) {
        logger.debug('AuthStore', 'Session validation error due to network issue, not logging out', { 
          error: error instanceof Error ? error.message : String(error)
        });
        return false;
      }

      // Real error - logout
      logger.error('AuthStore', 'Session validation error', { 
        error: error instanceof Error ? error.message : String(error) 
      });
      await get().signOut();
      return false;
    }
  },

  // Recovery mechanism for corrupted sessions
  recoverSession: async (): Promise<boolean> => {
    logger.warn('AuthStore', 'Attempting session recovery');
    
    try {
      // Clear potentially corrupted auth token
      const authKey = `sb-sgwqrcujqguximtbtjys-auth-token`;
      localStorage.removeItem(authKey);
      sessionStorage.removeItem(authKey);
      
      // Force a fresh session check
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        logger.info('AuthStore', 'Session recovery failed - no fresh session');
        return false;
      }
      
      // Validate the fresh session
      const isValid = await get().validateSession(session);
      if (!isValid) {
        logger.warn('AuthStore', 'Fresh session still invalid - signing out');
        await supabase.auth.signOut();
        return false;
      }
      
      logger.info('AuthStore', 'Session recovery successful');
      set({
        session,
        user: session.user,
        sessionValidated: true
      });
      return true;
    } catch (error) {
      logger.error('AuthStore', 'Session recovery failed', { 
        error: error instanceof Error ? error.message : String(error) 
      });
      return false;
    }
  },

  // Helper to detect network errors vs real auth failures
  isNetworkError: (error: any): boolean => {
    const errorMessage = error?.message || String(error);
    return errorMessage.includes('ERR_CONNECTION_CLOSED') ||
           errorMessage.includes('ERR_INTERNET_DISCONNECTED') ||
           errorMessage.includes('ERR_NETWORK_CHANGED') ||
           errorMessage.includes('ERR_CONNECTION_REFUSED') ||
           errorMessage.includes('Failed to fetch') ||
           errorMessage.includes('NetworkError') ||
           errorMessage.includes('fetch failed');
  }
});